import React, { useEffect, useRef, useState } from 'react';
import SectionHeader from '../../common/section-header';
import portfolioData from '../../../data/portfolio.json';
import { FaAward, FaCertificate, FaMedal, FaTrophy, FaGraduationCap, FaRibbon, FaStar, FaShieldAlt } from 'react-icons/fa';
import "./style.css";

const ResumeSection = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);

  const projectsRef = useRef(null);
  const certificatesRef = useRef(null);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'resume') {
        setActiveTab('projects');
        if (timelineRef.current) {
          timelineRef.current.classList.add('visible');
        }
      }
    };
    const currentHash = window.location.hash.replace('#', '');
    if (currentHash === 'resume') {
      setActiveTab('projects');
      if (timelineRef.current) {
        timelineRef.current.classList.add('visible');
      }
    }
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handleHashChange);
    };
  }, []);

  useEffect(() => {
    const sectionNode = sectionRef.current;
    const timelineNode = timelineRef.current;
    const projectsNode = projectsRef.current;
    const certificatesNode = certificatesRef.current;

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const projectsObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      },
      { threshold: 0, rootMargin: '200px 0px' }
    );

    const certificatesObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          const cards = entry.target.querySelectorAll('.certificate-card');
          cards.forEach((card) => {
            card.classList.add('animate');
          });
        }
      },
      { threshold: 0, rootMargin: '200px 0px' }
    );

    if (sectionNode) sectionObserver.observe(sectionNode);
    if (timelineNode) {
      timelineNode.classList.add('visible');
      sectionObserver.observe(timelineNode);
    }
    if (projectsNode && activeTab === 'projects') {
      projectsObserver.observe(projectsNode);
    }
    if (certificatesNode && activeTab === 'certificates') {
      certificatesObserver.observe(certificatesNode);
      const cards = certificatesNode.querySelectorAll('.certificate-card');
      if (cards.length > 0) {
        requestAnimationFrame(() => {
          cards.forEach((card) => {
            card.classList.add('animate');
          });
        });
      }
    }

    return () => {
      if (sectionNode) sectionObserver.unobserve(sectionNode);
      if (timelineNode) sectionObserver.unobserve(timelineNode);
      if (projectsNode) projectsObserver.unobserve(projectsNode);
      if (certificatesNode) certificatesObserver.unobserve(certificatesNode);
    };
  }, [activeTab]);

  const getTechnologies = (project) => {
    const techs = [];
    const desc = project.description?.toLowerCase() || '';
    const title = (project.title || '').toLowerCase();
    if (title === 'autoor') {
      if (desc.includes('node.js') || desc.includes('nodejs')) techs.push('Node.js');
      if (desc.includes('express')) techs.push('Express');
      if (desc.includes('postgresql') || desc.includes('postgres')) techs.push('PostgreSQL');
      if (desc.includes('bootstrap')) techs.push('Bootstrap 5');
      if (desc.includes('vanilla js') || desc.includes('vanilla javascript')) techs.push('Vanilla JS');
      if (desc.includes('google maps') || desc.includes('google-maps')) techs.push('Google Maps');
      return techs;
    }
    if (title === 'cinemor') {
      techs.push('React', 'Spring Boot', 'PostgreSQL');
      return techs;
    }
    techs.push('HTML', 'CSS');
    if (desc.includes('node.js') || desc.includes('nodejs')) techs.push('Node.js');
    if (desc.includes('express')) techs.push('Express');
    if (desc.includes('postgresql') || desc.includes('postgres')) techs.push('PostgreSQL');
    if (desc.includes('react')) techs.push('React');
    if (desc.includes('bootstrap')) {
      if (desc.includes('bootstrap 5')) techs.push('Bootstrap 5');
      else techs.push('Bootstrap');
    }
    if (desc.includes('vanilla js') || desc.includes('vanilla javascript')) techs.push('Vanilla JS');
    if (desc.includes('javascript') && !desc.includes('vanilla') && !desc.includes('vanilla js')) techs.push('JavaScript');
    if (desc.includes('next.js')) techs.push('Next.js');
    if (desc.includes('sass')) techs.push('Sass');
    if (desc.includes('google maps') || desc.includes('google-maps')) techs.push('Google Maps');
    if (desc.includes('bootstrap icons')) techs.push('Bootstrap Icons');
    
    return techs;
  };

  const projects = portfolioData.map((project) => {
    const categoryDisplay = project.category
      ? project.category.filter(cat => cat !== 'alle').join(' • ')
      : 'Web Application';

    return {
      id: project.id,
      year: project.year || '2024',
      title: project.title,
      company: categoryDisplay,
      description: project.description,
      link: project.demoLink,
      linkLabel: project.linkLabel,
      githubLink: project.githubLink,
      technologies: project.technologies?.length ? project.technologies : getTechnologies(project),
      image: project.image
    };
  });

  const certificates = [
    { name: "Fullstack Java Developer Zertifikat" },
    { name: "Career Goal Setting - Karriere Zielsetzung" },
    { name: "Grundlagen des digitalen Marketings" },
    { name: "Gleichstellung der Geschlechter" },
    { name: "Website-Benutzerfreundlichkeit" },
    { name: "ProSchool IT-Kurs Zertifikat" },
    { name: "Technologie Schule Zertifizierung" },
    { name: "Stressbewältigung und Achtsamkeit" }
  ];

  return (
    <section id="resume" className="resume-section" ref={sectionRef}>
      <div className="resume-container">
        <SectionHeader title="Portfolio" subTitle="Meine Projekte & Zertifikate" />
        <div className="resume-tabs">
          <button 
            className={activeTab === 'projects' ? 'active' : ''}
            onClick={() => setActiveTab('projects')}
          >
            Projekte
          </button>
          <button
            className={activeTab === 'certificates' ? 'active' : ''}
            onClick={() => {
              setActiveTab('certificates');
              setTimeout(() => {
                const certificatesGrid = document.querySelector('.certificates-grid');
                if (certificatesGrid) {
                  const cards = certificatesGrid.querySelectorAll('.certificate-card');
                  cards.forEach((card) => {
                    card.classList.add('animate');
                  });
                }
              }, 100);
            }}
          >
            Zertifikate
          </button>
        </div>
        <div className="timeline-wrapper" ref={timelineRef}>
          {activeTab === 'projects' && (
            <div className="projects-container" ref={projectsRef}>
              {projects.map((item, idx) => {
                const isEven = idx % 2 === 0;
                return (
                  <div
                    key={item.id} 
                    className={`project-card ${isEven ? 'image-left' : 'image-right'}`}
                  >
                    <div className="project-image-wrapper">
                      <img 
                        src={`/img/${item.image}`} 
                        alt={item.title}
                        className="project-image"
                        loading="lazy"
                      />
                      <div className="project-image-overlay"></div>
          </div>
                    <div className="project-content">
                      <h3 className="project-title">{item.title}</h3>
                      <div className="project-category">{item.company}</div>
                      <p className="project-description">{item.description}</p>
                      
                      {item.technologies && item.technologies.length > 0 && (
                        <div className="project-technologies">
                          <strong>Technologien:</strong>
                          <div className="tech-tags">
                            {item.technologies.map((tech, techIndex) => (
                              <span key={techIndex} className="tech-tag">{tech}</span>
                            ))}
          </div>
        </div>
                      )}
                      
                      {(item.link || item.githubLink) && (
                        <div className="project-links">
                          {item.link && (
                            <a 
                              href={item.link} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="project-link-button"
                            >
                              <span>{item.linkLabel || 'Demo besuchen'}</span>
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </a>
                          )}
                          {item.githubLink && (
                            <a 
                              href={item.githubLink} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="project-link-button secondary"
                            >
                              <span>GitHub</span>
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </a>
                          )}
          </div>
                      )}
          </div>
        </div>
                );
              })}
            </div>
          )}

          {activeTab === 'certificates' && (
            <div className="certificates-grid" ref={certificatesRef}>
              {certificates.map((cert, index) => {
                const icons = [
                  <FaAward key="award" />,
                  <FaCertificate key="cert" />,
                  <FaMedal key="medal" />,
                  <FaTrophy key="trophy" />,
                  <FaGraduationCap key="grad" />,
                  <FaRibbon key="ribbon" />,
                  <FaStar key="star" />,
                  <FaShieldAlt key="shield" />
                ];
                const IconComponent = icons[index % icons.length];
                return (
                  <div key={index} className="certificate-card animate">
                    <div className="certificate-icon">
                      {IconComponent}
                    </div>
                    <h4 className="certificate-name">{cert.name}</h4>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ResumeSection;
